import {PureComponent, ReactNode, RefObject} from 'react';
import {TooltipPlacement} from './types';
import {join} from '../utils';
import styles from './styles.module.less';
import {createPortal} from 'react-dom';
import {jQuery} from './safe-jquery';

export class Tooltip extends PureComponent<TooltipProps, TooltipState> {
	public override readonly state = new TooltipState();

	get element() {
		if (this.props.element) {
			return this.props.element;
		}
		if (this.props.elementRef && this.props.elementRef.current) {
			return this.props.elementRef.current;
		}
		return null;
	}

	get arrowSize() {
		return this.props.arrowSize || 5;
	}

	// In some cases tooltip hasn't to appear immediately.
	get isInitiallyHidden() {
		return !this.props.showOnInit;
	}

	get className() {
		let out = 'tooltip fade in ';
		if (this.props.placement) {
			out += this.props.placement + ' ';
		}
		if (this.props.className) {
			out += this.props.className + ' ';
		}
		if (this.isInitiallyHidden) {
			out += 'tooltip-hidden';
		}
		return out;
	}

	protected rootElement: HTMLDivElement;
	protected hideTimerID: number = null;

	public override componentDidMount(): void {

		if (!this.element) {
			return;
		}

		const {x, y} = this.recalculatePosition();
		this.setState({x: x, y: y});

		if (!this.isInitiallyHidden) {
			this.showTooltip();
		}
	}

	public override componentWillUnmount() {
		this.clearHideTimeout();
	}

	public override render() {

		if (this.state.x == 0 && this.state.y == 0) {
			return null;
		}

		if (!this.element && !this.props.elementRef) {
			return null;
		}

		let container = null;
		if (this.props.container) {
			if (typeof (this.props.container) === 'string') {
				// @ts-ignore
				container = document[this.props.container];
			} else {
				container = this.props.container;
			}
		}

		return createPortal(<div ref={r => {
			this.onRootElementRefProvided(r, this.rootElement);
			this.rootElement = r;
		}}
		                                  className={join(this.className, !this.state.shown && styles.hidden)}
		                                  style={{
			                                  top: this.state.y,
			                                  left: this.state.x,
			                                  position: this.props.absolute ? 'absolute' : 'fixed',
		                                  }}>
			<div className='tooltip-arrow'/>
			<div className={join('tooltip-inner', this.props.innerClassName)} role='tooltip'>{this.props.children}</div>
		</div>, container ?? document.body);
	}

	protected onRootElementRefProvided(value?: HTMLDivElement, prevValue?: HTMLDivElement) {
		if (value) {
			this.initTooltipListeners(value);
		}
	}

	protected onMouseEnterOverTooltipHandler() {
		this.clearHideTimeout();
	}

	protected onMouseLeaveTooltipHandler() {
		this.hideTooltip();
	}

	protected showTooltip(hide: boolean = true, innerClassName?: string) {
		this.clearHideTimeout();
		jQuery()(this.rootElement).stop().fadeTo(300, 1);
		this.setState({innerClassName});
		const {x, y} = this.recalculatePosition();
		if (x !== this.state.x || y !== this.state.y) {
			this.setState({x: x, y: y, shown: true});
		} else {
			this.setState({shown: true});
		}
	}

	protected hideTooltip() {
		this.clearHideTimeout();
		this.hideTimerID = window.setTimeout(() => {
			jQuery()(this.rootElement).stop().fadeTo(230, 0);
			this.setState({shown: false});
		}, 70);
	}

	protected recalculatePosition() {
		if (!this.element || !this.rootElement) {
			return;
		}
		const {top, left} = this.getOffsetRelativeToBody(this.element);
		const offset = {width: this.element.offsetWidth, height: this.element.offsetHeight};
		const tooltipOffset = {width: this.rootElement.offsetWidth, height: this.rootElement.offsetHeight};

		let x = 0;
		let y = 0;

		if (this.props.placement == 'bottom') {
			x = Math.round(left + offset.width / 2 - tooltipOffset.width / 2);
			y = top + offset.height;
		}

		if (this.props.placement == 'left-bottom') {
			x = left;
			y = top + offset.height + 7;
		}

		if (this.props.placement == 'right') {
			x = left + offset.width + this.arrowSize;
			y = Math.round(top + offset.height / 2 - tooltipOffset.height / 2);
		}

		if (this.props.placement == 'left') {
			x = left - tooltipOffset.width + this.arrowSize * 2;
			y = Math.round(top + offset.height / 2 - tooltipOffset.height / 2);
		}

		if (this.props.placement == 'top') {
			x = Math.round(left + offset.width / 2 - tooltipOffset.width / 2);
			y = top - tooltipOffset.height;
		}
		return {x: x + (this.props.offsetX || 0), y: y + (this.props.offsetY || 0)};
	}

	protected getOffsetRelativeToBody(el: HTMLElement) {
		const rect = el.getBoundingClientRect(),
			scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
			scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		return {top: rect.top + scrollTop, left: rect.left + scrollLeft};
	}

	private initTooltipListeners(elementRef: HTMLDivElement) {
		if (!this.props.notHide) {
			elementRef.addEventListener('mouseenter', () => this.onMouseEnterOverTooltipHandler());
			elementRef.addEventListener('mouseleave', () => this.onMouseLeaveTooltipHandler());
		}
	}

	clearHideTimeout() {
		if (this.hideTimerID) {
			clearTimeout(this.hideTimerID);
		}
	}
}

export class TooltipState {
	x: number;
	y: number;
	shown: boolean;
	innerClassName?: string;
}

export class TooltipProps {
	offsetX?: number;
	offsetY?: number;
	element: HTMLElement;
	elementRef?: RefObject<HTMLElement>;
	container?: HTMLElement | string;
	placement?: TooltipPlacement;
	className?: string;
	arrowSize?: number;
	notHide?: boolean;
	// this field is used to imperatively show tooltip
	show?: number = 0;
	absolute?: boolean;
	children?: ReactNode;
	innerClassName?: string;
	showOnInit?: boolean;
}
